import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Modal from "../Model";
import { useAuthz } from "../../auth/useAuthz";
import LayoutEditor from "./LayoutEditor";
import CollectionsPanel from "./CollectionsPanel";
import {
  GET_HOME_LAYOUTS, GET_COLLECTIONS, SAVE_HOME_LAYOUT, DUPLICATE_HOME_LAYOUT, DELETE_HOME_LAYOUT,
} from "./graphql";
import { TABS, STATUS_STYLES, formatDate } from "./labels";

const NewLayoutForm = ({ gender, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [save, { loading }] = useMutation(SAVE_HOME_LAYOUT);
  const submit = async (e) => {
    e.preventDefault();
    const res = await save({ variables: { data: { name, gender, searchHints: [] } } });
    const out = res.data?.saveHomeLayout;
    if (out?.status !== "SUCCESS") { setError(out?.message || "Could not create"); return; }
    onCreated(out.id);
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">New {gender === "Female" ? "Salon" : "Bridal"} layout</h2>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Name</span>
        <input autoFocus className="border rounded-md px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} placeholder="Diwali home" />
        <span className="text-xs text-gray-500">Starts as a draft. Tip: duplicating the live layout is usually faster.</span>
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
        <button type="submit" disabled={loading || !name.trim()} className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold disabled:opacity-60">Create</button>
      </div>
    </form>
  );
};

/**
 * Home builder: the sections customers see on the app's Salon and Bridal home
 * pages, and the collections of services those sections show.
 */
const HomeBuilder = () => {
  const { can } = useAuthz();
  const canEdit = can("banners.edit");
  const [tab, setTab] = useState("layouts");
  const [gender, setGender] = useState("Female");
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const layoutsQuery = useQuery(GET_HOME_LAYOUTS, { variables: { gender }, fetchPolicy: "network-only" });
  const collectionsQuery = useQuery(GET_COLLECTIONS, { fetchPolicy: "network-only" });
  const [duplicate] = useMutation(DUPLICATE_HOME_LAYOUT);
  const [remove] = useMutation(DELETE_HOME_LAYOUT);

  const layouts = layoutsQuery.data?.getHomeLayouts?.layouts || [];
  const collections = collectionsQuery.data?.getCollections?.collections || [];
  const now = new Date();
  const servingNow = (l) => l.status === "LIVE" && (!l.startAt || new Date(l.startAt) <= now) && (!l.endAt || new Date(l.endAt) >= now);

  const doDuplicate = async (l) => {
    // eslint-disable-next-line no-alert
    const name = window.prompt("Name for the copy", `${l.name} (copy)`);
    if (!name) return;
    const res = await duplicate({ variables: { id: l.id, name } });
    const out = res.data?.duplicateHomeLayout;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") { window.alert(out?.message || "Could not duplicate"); return; }
    layoutsQuery.refetch();
    setOpenId(out.id);
  };
  const doDelete = async (l) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${l.name}"? This cannot be undone.`)) return;
    const res = await remove({ variables: { id: l.id } });
    const out = res.data?.deleteHomeLayout;
    // eslint-disable-next-line no-alert
    if (out?.status !== "SUCCESS") window.alert(out?.message || "Could not delete");
    layoutsQuery.refetch();
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="mb-6">
          <h1 className="text-xl font-semibold">App home builder</h1>
          <p className="text-sm text-gray-500">
            Choose what customers see on the app's Salon and Bridal home pages: sections, their order, and the services in each rail.
          </p>
        </div>

        <div className="flex gap-2 mb-4">
          {[["layouts", "Home layouts"], ["collections", "Collections"]].map(([k, label]) => (
            <button key={k} onClick={() => { setTab(k); setOpenId(null); }} className={`px-4 py-2 rounded ${tab === k ? "bg-blue-600 text-white" : "bg-gray-200"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "layouts" && openId && (
          <LayoutEditor
            layoutId={openId}
            collections={collections}
            canEdit={canEdit}
            onBack={() => { setOpenId(null); layoutsQuery.refetch(); }}
            onChanged={() => { layoutsQuery.refetch(); collectionsQuery.refetch(); }}
          />
        )}

        {tab === "layouts" && !openId && (
          <div className="bg-white rounded-lg border">
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 border-b">
              <div className="flex gap-2">
                {TABS.map((t) => (
                  <button key={t.gender} onClick={() => setGender(t.gender)} className={`px-3 py-1.5 rounded-md text-sm ${gender === t.gender ? "bg-gray-900 text-white" : "bg-gray-100"}`}>
                    {t.label} tab
                  </button>
                ))}
              </div>
              {canEdit && <button onClick={() => setCreating(true)} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ New layout</button>}
            </div>
            <ul className="divide-y">
              {layoutsQuery.loading && <li className="px-3 py-6 text-center text-sm text-gray-500">Loading…</li>}
              {layouts.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpenId(l.id)} className="font-medium hover:underline text-left">{l.name}</button>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[l.status]}`}>{l.status.toLowerCase()}</span>
                      {servingNow(l) && <span className="px-2 py-0.5 rounded-full text-xs bg-green-600 text-white">in the app now</span>}
                    </div>
                    <p className="text-xs text-gray-500">
                      {l.startAt || l.endAt ? `${formatDate(l.startAt) || "now"} → ${formatDate(l.endAt) || "no end"}` : "Default layout"}
                      {l.publishedAt ? ` · v${l.version} published ${formatDate(l.publishedAt)}` : " · not published"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setOpenId(l.id)} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">Open</button>
                    {canEdit && <button onClick={() => doDuplicate(l)} className="px-3 py-1.5 rounded-md hover:bg-gray-100 text-sm">Duplicate</button>}
                    {canEdit && l.status !== "LIVE" && <button onClick={() => doDelete(l)} className="px-3 py-1.5 rounded-md hover:bg-red-50 text-red-600 text-sm">Delete</button>}
                  </div>
                </li>
              ))}
              {!layoutsQuery.loading && !layouts.length && (
                <li className="px-3 py-8 text-center text-sm text-gray-500">No layouts for this tab yet. The app shows its old home page until one is published.</li>
              )}
            </ul>
          </div>
        )}

        {tab === "collections" && (
          <CollectionsPanel collections={collections} loading={collectionsQuery.loading} canEdit={canEdit} onChanged={() => collectionsQuery.refetch()} />
        )}

        {creating && (
          <Modal onClose={() => setCreating(false)} disableOutsideClick>
            <NewLayoutForm gender={gender} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); layoutsQuery.refetch(); setOpenId(id); }} />
          </Modal>
        )}
      </Layout>
    </div>
  );
};

export default HomeBuilder;
